package top.wcpe.allincore.ui.runtime.ir.eval

import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.dsl.ir.model.action.IrFun
import top.wcpe.allincore.ui.runtime.host.AnimationHost
import top.wcpe.allincore.ui.runtime.host.TimerHost
import top.wcpe.allincore.ui.runtime.host.ToastManager
import top.wcpe.allincore.ui.runtime.host.UiAtomicRef
import top.wcpe.allincore.ui.runtime.host.UiExecutor
import top.wcpe.allincore.ui.runtime.ir.DslExecutor
import top.wcpe.allincore.ui.runtime.ir.DslLogger
import top.wcpe.allincore.ui.runtime.ir.api.ClientApi
import top.wcpe.allincore.ui.runtime.ir.api.DateApi
import top.wcpe.allincore.ui.runtime.ir.api.DebugApi
import top.wcpe.allincore.ui.runtime.ir.api.DisplayApi
import top.wcpe.allincore.ui.runtime.ir.api.LocalApi
import top.wcpe.allincore.ui.runtime.ir.api.PersistApi
import top.wcpe.allincore.ui.runtime.ir.api.PlaceholderApi
import top.wcpe.allincore.ui.runtime.ir.api.SessionApi
import top.wcpe.allincore.ui.runtime.ir.api.SlotApi
import top.wcpe.allincore.ui.runtime.ir.api.TimeApi
import top.wcpe.allincore.ui.runtime.ir.api.TimerApi
import top.wcpe.allincore.ui.runtime.ir.api.UiCommandApi
import top.wcpe.allincore.ui.runtime.ir.api.VanillaApi
import top.wcpe.allincore.ui.runtime.ir.call.LocalFunValue
import top.wcpe.allincore.ui.runtime.ir.request.RequestDispatcher
import top.wcpe.allincore.ui.runtime.ir.request.RequestRegistry
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateAccess
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateRegistry
import top.wcpe.allincore.ui.runtime.ir.subscribe.SubscribeRouter
import top.wcpe.allincore.ui.runtime.reactive.Computed
import top.wcpe.allincore.ui.runtime.reactive.Signal
import top.wcpe.allincore.ui.runtime.render.RendererRegistry
import top.wcpe.allincore.ui.runtime.theme.ThemeEnvironment
import top.wcpe.allincore.ui.runtime.theme.ThemeLookup
import top.wcpe.allincore.ui.runtime.theme.ThemeManager
import kotlin.jvm.JvmStatic

/**
 * 表达式求值期上下文：state 信号表、本地作用域（回调参数）、可选的 request 注册表与调度器、主题 lookup。
 * 不可变；通过 [withLocals] 派生新实例。
 *
 * C1 修复：`varFrames` 是 `var` 局部变量的帧栈。与 [locals]（callback 参数 / IdentRef）解耦：
 * - [locals] 不可变，由 [withLocals] 合并派生
 * - `varFrames` 是可变栈，每次 `ExprEvaluator.execute(stmts, ctx)` 入口都会 [pushVarFrame]，
 *   退出前 [popVarFrame]，保证多次触发同一 action / 多次投递同一 callback 互不污染
 * - [withLocals] 派生时会分配独立的新栈，确保 callback 与外层完全隔离
 */
@Suppress("TooManyFunctions", "LongParameterList")
class EvalContext private constructor(
    states: Map<String, Signal<Any?>>,
    stateTypes: Map<String, IrType>?,
    locals: Map<String, Any?>,
    registry: RequestRegistry?,
    requestDispatcher: RequestDispatcher?,
    themeLookup: ThemeLookup?,
    localApi: LocalApi?,
    varFrames: ArrayDeque<MutableMap<String, Any?>>?,
    localFunFrames: ArrayDeque<MutableMap<String, LocalFunValue>>?,
    screenFunctions: Map<String, IrFun>?,
    logger: DslLogger?,
    executor: DslExecutor?,
    themeManager: ThemeManager?,
    rendererRegistryHolder: UiAtomicRef<RendererRegistry>?,
    slotApi: SlotApi?,
    placeholderApi: PlaceholderApi?,
    displayApi: DisplayApi? = null,
    params: Map<String, Any?> = emptyMap(),
    sharedStateAccess: SharedStateAccess? = null,
    themeEnvironment: ThemeEnvironment? = null,
) : EvalContextHandle,
    OverlayEvalContext {
    // 实现 EvalContextHandle：UIElement（commonMain）经该中立句柄持有本上下文（marker 断环）。
    // 实现 OverlayEvalContext：InputDispatcher（commonMain）经该中立 marker 透传求值上下文给浮层编排缝，
    // 由 jvm actual cast 回本类型（见 OverlayEvalContext / OverlayOrchestrator）。

    val states: Map<String, Signal<Any?>> = states

    /**
     * M5-c #I5：state 字段名 → 静态类型。仅包含 DSL 作者显式书写类型的字段；未写的字段不入表，
     * 等价于 [IrType.ANY]，保持对未标注 .allin 资源的零破坏兼容。
     */
    val stateTypes: Map<String, IrType> = stateTypes ?: emptyMap()
    val locals: Map<String, Any?> = locals
    val params: Map<String, Any?> = LinkedHashMap(params)
    val registry: RequestRegistry? = registry
    val requestDispatcher: RequestDispatcher? = requestDispatcher
    val sharedStateAccess: SharedStateAccess = sharedStateAccess ?: SharedStateRegistry.global()
    val themeEnvironment: ThemeEnvironment = themeEnvironment ?: ThemeEnvironment(
        themeLookup ?: ThemeLookup.empty(),
        themeManager ?: ThemeManager.EMPTY,
        emptyList(),
    )
    val themeLookup: ThemeLookup get() = themeEnvironment.lookup()
    val localApi: LocalApi = localApi ?: LocalApi.NOOP

    /** var 局部变量帧栈；栈顶为当前活动帧。 */
    private val varFrames: ArrayDeque<MutableMap<String, Any?>> = varFrames ?: newVarStack()

    /** 局部 fun 帧栈（C4）；与 varFrames 同步 push/pop，但单独命名空间。 */
    private val localFunFrames: ArrayDeque<MutableMap<String, LocalFunValue>> = localFunFrames ?: newLocalFunStack()

    /** screen 级 fun 表（C4，扁平，不可变）；空 map 表示当前上下文无 fun。 */
    val screenFunctions: Map<String, IrFun> = screenFunctions ?: emptyMap()

    /** DSL 内建 print/log 输出端；默认 [DslLogger.STDOUT]。 */
    val logger: DslLogger = logger ?: DslLogger.STDOUT

    /** DSL 执行调度器；null 表示同步模式（测试 / 旧路径兼容）。 */
    val executor: DslExecutor? = executor

    /** 槽位物品读取 API；默认实现返回空槽视图。 */
    val slotApi: SlotApi = slotApi ?: SlotApi.NOOP

    /** placeholder 读取 API；默认实现返回不可用 entry。 */
    val placeholderApi: PlaceholderApi = placeholderApi ?: PlaceholderApi.NOOP

    /** 展示物品读取 API（display.read）；默认实现返回空视图。 */
    val displayApi: DisplayApi = displayApi ?: DisplayApi.NOOP

    /**
     * 主题管理器：持有 IR 中所有可用 scheme 的预设，供 `local.setTheme` 切换使用。
     * 非 null（默认 [ThemeManager.EMPTY]），保证 `local.setTheme` 调用路径不空指针。
     */
    val themeManager: ThemeManager = this.themeEnvironment.themeManager

    /**
     * 渲染器注册表引用：`local.setTheme` 通过本字段写入 controlTheme。使用 [AtomicReference] 让所有派生
     * 上下文（[withLocals] / [withSlotApi] / [withPlaceholderApi] 等）共享同一持有器，防止 IrSession 链式
     * with* 调用之后绑定 root EvalContext 而派生上下文仍为 null（或反之）的可见性割裂问题。
     * 派生路径必须复用同一持有器；root 路径若传 null 则创建新持有器并允许后续 bind。
     */
    private val rendererRegistryRef: UiAtomicRef<RendererRegistry> = rendererRegistryHolder ?: UiAtomicRef()

    /**
     * derived 派生值表：字段名 → 响应式 [Computed]。由 IrInterpreter 在 session 创建后通过 [attachDerived]
     * 注入；派生上下文共享同一引用。未声明 `derived defaults` 时为空 map。
     */
    var derived: Map<String, Computed<Any?>> = emptyMap()
        private set

    /** M6-h Round 2：toast 管理器；由 IrInterpreter 在 session 创建时通过 [attachToastManager] 注入；为 null 时 toast() 内建调用静默忽略。 */
    var toastManager: ToastManager? = null
        private set

    /**
     * M6-i：动画宿主，提供按 id 查找元素 + 按名播放动画的能力。使用 [AtomicReference] 让所有派生上下文共享
     * 同一持有器：IrInterpreter.interpretSession 在 buildElement(root) 之后才 attachAnimationHost，而虚拟容器
     * 子项 lambda 在 buildElement 期间已经创建并捕获 derived ctx 闭包，若按值复制，后续 attach 不会传播。
     */
    private var animationHostRef: UiAtomicRef<AnimationHost> = UiAtomicRef()

    /** 特性②:渲染线程执行器。DSL 线程通过它把 ui("id").method() 的写动作 post 回渲染线程，避免与 tick/输入并发改元素状态。 */
    var renderThreadExecutor: UiExecutor? = null
        private set

    /** 任务 3 / 4：通用 await 时间调度宿主。session.dispose 时触发 host.dispose 取消所有 timer。 */
    var timerHost: TimerHost? = null
        private set

    /** B7：订阅路由器；派生上下文沿用同一引用。默认 [SubscribeRouter.NOOP]。 */
    var subscribeRouter: SubscribeRouter = SubscribeRouter.NOOP
        private set

    /**
     * v4 §E.3：`client` 内建作用域 API，`client.world.*` 经此读取 WorldDataSource。共享 [AtomicReference]
     * 持有器，默认包裹 [ClientApi.NOOP]，保证未接入平台数据源的链路读 `client.world.posX` 得 0、
     * `entities(48)` 得空列表。
     */
    private var clientApiRef: UiAtomicRef<ClientApi> = UiAtomicRef(ClientApi.NOOP)

    // Phase 1：统一注入层。以下 API 字段通过 [builtin] 统一查找，全部为运行时注入字段，默认 NOOP；
    // 由 IrInterpreter 在 session 创建时通过对应 attach 方法注入真实实例。派生上下文通过 inheritRuntimeAttachments 共享同一引用。
    var vanillaApi: VanillaApi = VanillaApi.NOOP
        private set
    var timeApi: TimeApi = TimeApi.NOOP
        private set
    var dateApi: DateApi = DateApi.NOOP
        private set
    var timerApi: TimerApi = TimerApi.NOOP
        private set
    var debugApi: DebugApi = DebugApi.NOOP
        private set

    /**
     * per-session 的 UiCommandApi；使用 [AtomicReference] 持有器，与 animationHost 同款共享模式。
     * 虚拟容器子项的 lambda 在 buildElement 期间派生 ctx 后捕获闭包，IrInterpreter 在 buildElement 完成后才 attachUiApi。
     */
    private var uiApiHolder: UiAtomicRef<UiCommandApi> = UiAtomicRef()
    var sessionApi: SessionApi = SessionApi.NOOP
        private set
    var persistApi: PersistApi = PersistApi.NOOP
        private set

    /**
     * 派生一个带新 locals 作用域的上下文，其余字段共享；同时分配独立的 var 帧栈与局部 fun 栈，让 callback 体不污染外层。
     */
    fun withLocals(newLocals: Map<String, Any?>?): EvalContext {
        val merged = LinkedHashMap(locals)
        if (newLocals != null) merged.putAll(newLocals)
        val derived = EvalContext(
            states, stateTypes, LinkedHashMap(merged), registry,
            requestDispatcher, themeLookup, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions, logger, executor, themeManager, rendererRegistryRef, slotApi, placeholderApi,
            displayApi,
            params = params,
            sharedStateAccess = sharedStateAccess,
            themeEnvironment = themeEnvironment,
        )
        return inheritRuntimeAttachments(derived)
    }

    /** 派生一个注入只读打开参数的上下文，其余运行期宿主共享。 */
    fun withParams(newParams: Map<String, Any?>?): EvalContext {
        val copied = LinkedHashMap<String, Any?>()
        if (newParams != null) copied.putAll(newParams)
        val derived = EvalContext(
            states, stateTypes, locals, registry,
            requestDispatcher, themeLookup, localApi, varFrames, localFunFrames,
            screenFunctions, logger, executor, themeManager, rendererRegistryRef, slotApi, placeholderApi,
            displayApi,
            params = copied,
            sharedStateAccess = sharedStateAccess,
            themeEnvironment = themeEnvironment,
        )
        return inheritRuntimeAttachments(derived)
    }

    /** 渲染器注册表引用：可能为 null（纯逻辑测试上下文中无 UI）。 */
    val rendererRegistry: RendererRegistry? get() = rendererRegistryRef.get()

    /**
     * 一次性绑定渲染器注册表。调用时机：客户端创建 RenderRoot 后，把 renderRoot.registry() 绑定到本上下文，
     * 让 `local.setTheme` 能写入 controlTheme。语义：通常仅在 session 初始化阶段调用一次；重复调用会覆盖旧引用，无校验。
     */
    fun bindRendererRegistry(registry: RendererRegistry?) {
        rendererRegistryRef.set(registry)
    }

    /** 派生一个注入槽位读取 API 的上下文，其余字段共享。 */
    fun withSlotApi(nextSlotApi: SlotApi?): EvalContext {
        val derived = EvalContext(
            states, stateTypes, locals, registry,
            requestDispatcher, themeLookup, localApi, varFrames, localFunFrames,
            screenFunctions, logger, executor, themeManager, rendererRegistryRef, nextSlotApi, placeholderApi,
            displayApi,
            params = params,
            sharedStateAccess = sharedStateAccess,
            themeEnvironment = themeEnvironment,
        )
        return inheritRuntimeAttachments(derived)
    }

    /** 派生一个注入 placeholder 读取 API 的上下文，其余字段共享。 */
    fun withPlaceholderApi(nextPlaceholderApi: PlaceholderApi?): EvalContext {
        val derived = EvalContext(
            states, stateTypes, locals, registry,
            requestDispatcher, themeLookup, localApi, varFrames, localFunFrames,
            screenFunctions, logger, executor, themeManager, rendererRegistryRef, slotApi, nextPlaceholderApi,
            displayApi,
            params = params,
            sharedStateAccess = sharedStateAccess,
            themeEnvironment = themeEnvironment,
        )
        return inheritRuntimeAttachments(derived)
    }

    /** 派生一个注入展示物品读取 API 的上下文，其余字段共享。 */
    fun withDisplayApi(nextDisplayApi: DisplayApi?): EvalContext {
        val derived = EvalContext(
            states, stateTypes, locals, registry,
            requestDispatcher, themeLookup, localApi, varFrames, localFunFrames,
            screenFunctions, logger, executor, themeManager, rendererRegistryRef, slotApi, placeholderApi,
            nextDisplayApi,
            params = params,
            sharedStateAccess = sharedStateAccess,
            themeEnvironment = themeEnvironment,
        )
        return inheritRuntimeAttachments(derived)
    }

    /** 派生上下文继承运行期宿主引用，避免新增派生入口时遗漏。 */
    private fun inheritRuntimeAttachments(child: EvalContext): EvalContext {
        child.toastManager = toastManager
        child.animationHostRef = animationHostRef
        child.subscribeRouter = subscribeRouter
        // v4 §E.3：client 作用域 API 共享同一 AtomicReference 持有器
        child.clientApiRef = clientApiRef
        child.derived = this.derived
        // 任务 4：timer 宿主
        child.timerHost = timerHost
        child.renderThreadExecutor = renderThreadExecutor
        // Phase 1：统一注入层字段继承（共享引用，attach 后派生 ctx 也能看到）
        child.vanillaApi = vanillaApi
        child.timeApi = timeApi
        child.dateApi = dateApi
        child.timerApi = timerApi
        child.debugApi = debugApi
        child.uiApiHolder = uiApiHolder
        child.sessionApi = sessionApi
        child.persistApi = persistApi
        return child
    }

    /** M6-h Round 2：注入 toast 管理器。约定由 IrInterpreter 在 session 创建时调用一次；派生上下文共享同一引用。 */
    fun attachToastManager(mgr: ToastManager?) {
        toastManager = mgr
    }

    /** 注入 derived 派生值表。约定由 IrInterpreter 在 session 创建后调用一次；派生上下文共享同一引用。 */
    fun attachDerived(derivedTable: Map<String, Computed<Any?>>?) {
        derived = derivedTable ?: emptyMap()
    }

    /** M6-i：注入动画宿主；约定由 IrInterpreter 在 session 构造完成后调用一次。派生上下文共享同一引用。 */
    fun attachAnimationHost(host: AnimationHost?) {
        animationHostRef.set(host)
    }

    /** M6-i：当前动画宿主；未注入时返回 null（内建 animate() 将按未集成处理）。 */
    val animationHost: AnimationHost? get() = animationHostRef.get()

    /** 特性②:注入渲染线程执行器(平台在 session 创建后调用一次)。派生上下文共享同一引用。 */
    fun attachRenderThreadExecutor(executor: UiExecutor?) {
        renderThreadExecutor = executor
    }

    /** 任务 4：注入 timer 宿主；约定由 IrInterpreter 在 session 构造完成后调用一次。派生上下文共享同一引用。 */
    fun attachTimerHost(host: TimerHost?) {
        timerHost = host
    }

    /** B7：注入订阅路由器；约定由 IrInterpreter 在 session 创建时调用一次。派生上下文共享同一引用。 */
    fun attachSubscribeRouter(router: SubscribeRouter?) {
        subscribeRouter = router ?: SubscribeRouter.NOOP
    }

    /**
     * v4 §E.3：注入 `client` 作用域 API；约定由 IrInterpreter 在 session 创建时调用一次。
     * 共享 AtomicReference 持有器：调本方法后所有派生 ctx 立即看到新 API。
     */
    fun attachClientApi(api: ClientApi?) {
        clientApiRef.set(api ?: ClientApi.NOOP)
    }

    /** v4 §E.3：当前 `client` 作用域 API；永不为 null（默认 [ClientApi.NOOP]）。 */
    val clientApi: ClientApi get() = clientApiRef.get() ?: ClientApi.NOOP

    // ===== Phase 1：统一内建标识符查找 =====

    /**
     * 内建标识符统一查找表。返回 null 表示该 name 不是内建标识符，由调用方回退到 locals 查找。
     * 替代原来 ExprEvaluator 中直接引用 INSTANCE / PersistApiHolder 等静态单例的模式，统一通过 EvalContext 注入，测试可 mock。
     */
    fun builtin(name: String): Any? = when (name) {
        "local" -> localApi
        "client" -> clientApi
        "slot" -> slotApi
        "display" -> displayApi
        "papi" -> placeholderApi
        "vanilla" -> vanillaApi
        "time" -> timeApi
        "date" -> dateApi
        "timer" -> timerApi
        "debug" -> debugApi
        // uiApiHolder 在 attachUiApi 之前 get() 为 null，退化为安全默认值
        "ui" -> uiApiHolder.get() ?: UiCommandApi(null, null)
        "session" -> sessionApi
        "persist" -> persistApi
        else -> null
    }

    /** Phase 1：ui API getter；可能为 null（per-session 注入）。 */
    val uiApi: UiCommandApi? get() = uiApiHolder.get()

    /** Phase 1：注入 vanilla API。 */
    fun attachVanillaApi(api: VanillaApi?) {
        vanillaApi = api ?: VanillaApi.NOOP
    }

    /** Phase 1：注入 time API。 */
    fun attachTimeApi(api: TimeApi?) {
        timeApi = api ?: TimeApi.NOOP
    }

    /** Phase 1：注入 date API。 */
    fun attachDateApi(api: DateApi?) {
        dateApi = api ?: DateApi.NOOP
    }

    /** Phase 1：注入 timer API。 */
    fun attachTimerApi(api: TimerApi?) {
        timerApi = api ?: TimerApi.NOOP
    }

    /** Phase 1：注入 debug API。 */
    fun attachDebugApi(api: DebugApi?) {
        debugApi = api ?: DebugApi.NOOP
    }

    /** Phase 1：注入 ui API（per-session，持有 AnimationHost + UiExecutor）。 */
    fun attachUiApi(api: UiCommandApi?) {
        uiApiHolder.set(api)
    }

    /** Phase 1：注入 session API。 */
    fun attachSessionApi(api: SessionApi?) {
        sessionApi = api ?: SessionApi.NOOP
    }

    /** Phase 1：注入 persist API。 */
    fun attachPersistApi(api: PersistApi?) {
        persistApi = api ?: PersistApi.NOOP
    }

    // ===== C1：var 局部变量帧栈 =====

    /** 进入一个新的 var 作用域（action body / callback body / 任意 {} block）。 */
    fun pushVarFrame() {
        varFrames.addFirst(LinkedHashMap())
        localFunFrames.addFirst(LinkedHashMap())
    }

    /** 离开当前 var 作用域；与 [pushVarFrame] 必须成对使用（建议 try-finally）。 */
    fun popVarFrame() {
        if (varFrames.size <= 1) {
            // 保留底帧；pop 越界视为内部错误
            throw IllegalStateException("popVarFrame 越过底帧")
        }
        varFrames.removeFirst()
        localFunFrames.removeFirst()
    }

    /** 在当前栈顶帧声明（或覆盖）一个局部变量。 */
    fun declareVar(name: String, value: Any?) {
        varFrames.first()[name] = value
    }

    /** 自顶向下查找变量；不存在返回 null（由调用方在 hasVar 失败时抛错）。 */
    fun getVar(name: String): Any? = findFrame(name)?.get(name)

    /** 自顶向下检查变量是否存在于任意可见帧。 */
    fun hasVar(name: String): Boolean = findFrame(name) != null

    /** 覆盖最近的可见帧中的变量；若都不存在则写入栈顶帧（与 declareVar 等价）。 */
    fun setVar(name: String, value: Any?) {
        val frame = findFrame(name)
        if (frame == null) {
            varFrames.first()[name] = value
        } else {
            frame[name] = value
        }
    }

    /** 自顶向下查找第一个包含 name 的帧。 */
    private fun findFrame(name: String): MutableMap<String, Any?>? {
        for (frame in varFrames) {
            if (frame.containsKey(name)) return frame
        }
        return null
    }

    // ===== C4：局部 fun 帧栈 / screen 级 fun 表 =====

    /** 在当前栈顶帧声明（或覆盖）一个局部 fun。 */
    fun declareLocalFun(name: String, value: LocalFunValue) {
        localFunFrames.first()[name] = value
    }

    /** 自顶向下查找局部 fun；不存在返回 null。 */
    fun findLocalFun(name: String): LocalFunValue? {
        for (frame in localFunFrames) {
            val v = frame[name]
            if (v != null) return v
        }
        return null
    }

    /**
     * 派生一个用于 screen 级 fun 调用的"干净"上下文：states/registry/theme/localApi/screenFunctions 共享，
     * locals 为空，var/fun 帧栈全新。语义：与 Kotlin 顶层函数对齐——不继承外层局部 var，但仍可访问 state / theme / localApi / 其它 screen 级 fun。
     */
    fun freshForScreenFun(): EvalContext {
        val derived = EvalContext(
            states, stateTypes, emptyMap(), registry, requestDispatcher, themeLookup, localApi,
            newVarStack(), newLocalFunStack(), screenFunctions, logger, executor,
            themeManager, rendererRegistryRef, slotApi, placeholderApi, displayApi,
            params = params,
            sharedStateAccess = sharedStateAccess,
            themeEnvironment = themeEnvironment,
        )
        return inheritRuntimeAttachments(derived)
    }

    /**
     * 把当前所有 var 帧扁平化为一份按值快照（C3 lambda 闭包捕获）。
     * 遍历顺序：从栈底（最外层）到栈顶（最内层），后写入覆盖先前同名条目，因此最终 map 中"内层覆盖外层"，与正常作用域查找语义一致。
     */
    fun snapshotVars(): Map<String, Any?> {
        val snapshot = LinkedHashMap<String, Any?>()
        // ArrayDeque 的 iterator() 是从头（栈顶）到尾（栈底）；asReversed 反向为栈底→栈顶
        for (frame in varFrames.asReversed()) {
            snapshot.putAll(frame)
        }
        return snapshot
    }

    companion object {
        /** 创建一个仅含一个空底帧的栈。底帧保证 declareVar 在任何时候都有可写入的帧。 */
        private fun newVarStack(): ArrayDeque<MutableMap<String, Any?>> {
            val stack = ArrayDeque<MutableMap<String, Any?>>()
            stack.addFirst(LinkedHashMap())
            return stack
        }

        /** 与 var 帧栈对偶的局部 fun 栈，初始含空底帧。 */
        private fun newLocalFunStack(): ArrayDeque<MutableMap<String, LocalFunValue>> {
            val stack = ArrayDeque<MutableMap<String, LocalFunValue>>()
            stack.addFirst(LinkedHashMap())
            return stack
        }

        /** 仅含 states 的最小上下文，registry/dispatcher 为空，themeLookup 为空。 */
        @JvmStatic
        fun of(states: Map<String, Signal<Any?>>): EvalContext = EvalContext(
            states, emptyMap(), emptyMap(), null, null,
            ThemeLookup.empty(), LocalApi.NOOP, newVarStack(), newLocalFunStack(),
            emptyMap(), DslLogger.STDOUT, null, ThemeManager.EMPTY, null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /** 完整上下文（含 request 支持），themeLookup 为空。 */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
        ): EvalContext = EvalContext(
            states, emptyMap(), emptyMap(), registry, requestDispatcher,
            ThemeLookup.empty(), LocalApi.NOOP, newVarStack(), newLocalFunStack(),
            emptyMap(), DslLogger.STDOUT, null, ThemeManager.EMPTY, null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /** 完整上下文（含 request 与 themeLookup）。 */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
        ): EvalContext = EvalContext(
            states, emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, LocalApi.NOOP, newVarStack(), newLocalFunStack(),
            emptyMap(), DslLogger.STDOUT, null, ThemeManager.EMPTY, null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /** 完整上下文（含 request、themeLookup 与 localApi）。 */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
            localApi: LocalApi?,
        ): EvalContext = EvalContext(
            states, emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, localApi, newVarStack(), newLocalFunStack(),
            emptyMap(), DslLogger.STDOUT, null, ThemeManager.EMPTY, null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /** 完整上下文（含 request、themeLookup、localApi 与 screen 级 functions，C4 入口）。 */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
            localApi: LocalApi?,
            screenFunctions: Map<String, IrFun>?,
        ): EvalContext = EvalContext(
            states, emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions ?: emptyMap(), DslLogger.STDOUT, null, ThemeManager.EMPTY,
            null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /** C6 完整上下文：在 C4 入口基础上注入自定义 logger。 */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
            localApi: LocalApi?,
            screenFunctions: Map<String, IrFun>?,
            logger: DslLogger?,
        ): EvalContext = EvalContext(
            states, emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions ?: emptyMap(), logger ?: DslLogger.STDOUT, null,
            ThemeManager.EMPTY, null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /**
         * M5-c #I5：完整入口，附带 state 静态类型表。`stateTypes` 仅包含 DSL 显式书写类型的 state 字段；
         * 缺省项不入表，写入时按 [IrType.ANY] 处理（不做强校验），保持向后兼容。
         */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            stateTypes: Map<String, IrType>?,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
            localApi: LocalApi?,
            screenFunctions: Map<String, IrFun>?,
            logger: DslLogger?,
        ): EvalContext = EvalContext(
            states, stateTypes ?: emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions ?: emptyMap(), logger ?: DslLogger.STDOUT, null,
            ThemeManager.EMPTY, null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /** 完整入口（含 DslExecutor）：平台层创建 session 时使用。 */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            stateTypes: Map<String, IrType>?,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
            localApi: LocalApi?,
            screenFunctions: Map<String, IrFun>?,
            logger: DslLogger?,
            executor: DslExecutor?,
        ): EvalContext = EvalContext(
            states, stateTypes ?: emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions ?: emptyMap(), logger ?: DslLogger.STDOUT, executor,
            ThemeManager.EMPTY, null, SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /**
         * 完整入口（含 DslExecutor + ThemeManager + RendererRegistry）：客户端 IrInterpreter 装配 session 时使用。
         *
         * @param themeManager IR 中所有可用主题预设；为 null 时按 [ThemeManager.EMPTY] 处理
         * @param rendererRegistry 渲染器注册表引用，`local.setTheme` 通过该引用切换 controlTheme；为 null 时 `local.setTheme` 调用会抛清晰错误
         */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            stateTypes: Map<String, IrType>?,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
            localApi: LocalApi?,
            screenFunctions: Map<String, IrFun>?,
            logger: DslLogger?,
            executor: DslExecutor?,
            themeManager: ThemeManager?,
            rendererRegistry: RendererRegistry?,
        ): EvalContext = EvalContext(
            states, stateTypes ?: emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions ?: emptyMap(), logger ?: DslLogger.STDOUT, executor,
            themeManager, UiAtomicRef(rendererRegistry), SlotApi.NOOP, PlaceholderApi.NOOP,
        )

        /** 完整入口增加共享状态域注入；未传入时仍使用全局注册表。 */
        @JvmStatic
        fun of(
            states: Map<String, Signal<Any?>>,
            stateTypes: Map<String, IrType>?,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeLookup: ThemeLookup?,
            localApi: LocalApi?,
            screenFunctions: Map<String, IrFun>?,
            logger: DslLogger?,
            executor: DslExecutor?,
            themeManager: ThemeManager?,
            rendererRegistry: RendererRegistry?,
            sharedStateAccess: SharedStateAccess?,
        ): EvalContext = EvalContext(
            states, stateTypes ?: emptyMap(), emptyMap(), registry, requestDispatcher,
            themeLookup, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions ?: emptyMap(), logger ?: DslLogger.STDOUT, executor,
            themeManager, UiAtomicRef(rendererRegistry), SlotApi.NOOP, PlaceholderApi.NOOP,
            sharedStateAccess = sharedStateAccess,
        )

        /** 每个 IrSession 的完整入口：注入共享 ThemeEnvironment 与可选 sharedStateAccess。 */
        @JvmStatic
        fun ofThemeEnvironment(
            states: Map<String, Signal<Any?>>,
            stateTypes: Map<String, IrType>?,
            registry: RequestRegistry?,
            requestDispatcher: RequestDispatcher?,
            themeEnvironment: ThemeEnvironment,
            localApi: LocalApi?,
            screenFunctions: Map<String, IrFun>?,
            logger: DslLogger?,
            executor: DslExecutor?,
            themeManager: ThemeManager?,
            rendererRegistry: RendererRegistry?,
            sharedStateAccess: SharedStateAccess? = null,
        ): EvalContext = EvalContext(
            states, stateTypes ?: emptyMap(), emptyMap(), registry, requestDispatcher,
            null, localApi, newVarStack(), newLocalFunStack(),
            screenFunctions ?: emptyMap(), logger ?: DslLogger.STDOUT, executor,
            themeManager ?: themeEnvironment.themeManager, UiAtomicRef(rendererRegistry), SlotApi.NOOP, PlaceholderApi.NOOP,
            displayApi = null,
            params = emptyMap(),
            sharedStateAccess = sharedStateAccess,
            themeEnvironment = themeEnvironment,
        )
    }
}
